<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left"><h1 class="page-title">{{ pageTitle }}</h1></div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="save">
          {{ saving ? '檢查並儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <form v-else class="form-container salon-form-page" @submit.prevent="save">
        <div class="form-section">
          <h2 class="section-title">規則範圍</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required-label">名稱</label>
              <input v-model.trim="form.name" class="form-control" maxlength="150" required />
            </div>
            <div v-if="showStoreStep" class="form-group">
              <label class="form-label required-label">門市</label>
              <select v-model="form.storeId" class="form-control" required @change="handleStoreChange">
                <option value="">請選擇</option>
                <option v-for="store in stores" :key="store.id" :value="store.id">{{ store.storeName }}</option>
              </select>
              <span v-if="!isEdit && !stores.length" class="form-hint">目前沒有開放美髮預約的門市。</span>
            </div>
            <template v-if="showDateStep">
            <div class="form-group">
              <label class="form-label required-label">{{ ruleType === 'closure' ? '休假模式' : '限制模式' }}</label>
              <select v-model="form.dateMode" class="form-control" @change="handleDateModeChange">
                <option value="single">單日</option>
                <option value="range">多日</option>
                <option v-if="ruleType === 'closure'" value="recurring">週期</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label required-label">
                {{ form.dateMode === 'single' ? '日期' : (form.dateMode === 'recurring' ? '週期生效日' : '開始日期') }}
              </label>
              <input
                v-model="form.startDate"
                type="date"
                class="form-control"
                required
                @change="handleStartDateChange"
              />
            </div>
            </template>
            <div v-if="form.dateMode !== 'single'" class="form-group">
              <label class="form-label" :class="{ 'required-label': form.dateMode !== 'recurring' }">
                {{ form.dateMode === 'recurring' ? '週期結束日' : '結束日期' }}
              </label>
              <input
                v-model="form.endDate"
                type="date"
                class="form-control"
                :required="form.dateMode !== 'recurring'"
              />
              <span v-if="form.dateMode === 'recurring'" class="form-hint">不填代表永久生效。</span>
            </div>
            <template v-if="showAdvancedStep">
            <label class="check-label">
              <input v-model="form.isAllDay" type="checkbox" @change="handleAllDayChange" /> 整日
            </label>
            <template v-if="!form.isAllDay">
              <div v-if="showBusinessSlotShortcuts" class="form-group business-slot-group">
                <label class="form-label">門市營業時段</label>
                <p v-if="hoursLoading" class="form-hint">營業時段載入中...</p>
                <template v-else-if="selectedBusinessSlots.length">
                  <div class="business-slot-buttons">
                    <button
                      v-for="slot in selectedBusinessSlots"
                      :key="`${slot.startTime}-${slot.endTime}`"
                      type="button"
                      class="business-slot-button"
                      :class="{ active: isSelectedBusinessSlot(slot) }"
                      @click="selectBusinessSlot(slot)"
                    >
                      {{ slot.startTime }}–{{ slot.endTime }}
                    </button>
                  </div>
                  <span class="form-hint">點選後帶入起訖時間，仍可自行調整。</span>
                </template>
                <span v-else class="form-hint">該日未設定營業時段，請自行輸入起訖時間。</span>
              </div>
              <div class="form-group">
                <label class="form-label required-label">開始時間</label>
                <input v-model="form.startTime" type="time" class="form-control" required />
              </div>
              <div class="form-group">
                <label class="form-label required-label">結束時間</label>
                <input v-model="form.endTime" type="time" class="form-control" required />
              </div>
            </template>
            </template>
          </div>
          <p v-if="blockCreateGuided && !showAdvancedStep" class="form-hint setup-hint">
            請先完成名稱、門市與日期，再設定時段、原因、設計師與服務。
          </p>
          <div v-if="showAdvancedStep" class="form-group">
            <label class="form-label">原因</label>
            <textarea v-model.trim="form.reason" class="form-control" rows="3"></textarea>
          </div>
        </div>

        <div v-if="ruleType === 'closure' && form.dateMode === 'recurring'" class="form-section">
          <h2 class="section-title">重複規則</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label">週期模式</label>
              <select v-model="form.recurrenceType" class="form-control">
                <option value="weekly">每週指定星期</option>
                <option value="monthly">每月指定日期</option>
              </select>
            </div>
          </div>
          <template v-if="form.recurrenceType === 'weekly'">
            <p class="form-label required-label">指定星期</p>
            <div>
              <label v-for="day in weekdays" :key="day.value" class="check-label">
                <input v-model="form.daysOfWeek" type="checkbox" :value="day.value" /> {{ day.label }}
              </label>
            </div>
          </template>
          <template v-else>
            <p class="form-label required-label">指定日期</p>
            <div class="month-days">
              <label v-for="day in monthDays" :key="day" class="check-label month-day">
                <input v-model="form.daysOfMonth" type="checkbox" :value="day" /> {{ day }} 日
              </label>
            </div>
          </template>
        </div>

        <template v-if="ruleType === 'block' && showAdvancedStep">
          <p v-if="blockMatchesStoreClosure" class="form-hint closure-warning">
            整日且未指定設計師與服務等同店休，請改用門市休假。
          </p>
          <div class="form-section">
            <h2 class="section-title">限制設計師</h2>
            <p class="form-hint">不選代表全店設計師。</p>
            <p v-if="form.storeId && !stylists.length" class="form-hint">此門市目前沒有可服務的設計師。</p>
            <label v-for="stylist in stylists" :key="stylist.id" class="check-label">
              <input
                v-model="form.stylistIds"
                type="checkbox"
                :value="stylist.id"
                @change="handleStylistsChange"
              />
              {{ stylist.displayName }}
            </label>
          </div>
          <div class="form-section">
            <h2 class="section-title">限制服務</h2>
            <p class="form-hint">不選代表全部服務。</p>
            <label v-for="service in services" :key="service.id" class="check-label">
              <input v-model="form.serviceIds" type="checkbox" :value="service.id" /> {{ service.name }}
            </label>
          </div>
        </template>
      </form>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { showError, showSuccess } from '@/utils/swal.js';

const emptyForm = () => ({
  storeId: '', name: '', dateMode: 'single', startDate: '', endDate: '', isAllDay: true,
  startTime: '', endTime: '', reason: '', recurrenceType: 'none',
  daysOfWeek: [], daysOfMonth: [], recurrenceInterval: 1, stylistIds: [], serviceIds: []
});

export default {
  name: 'SalonExceptionForm',
  data() {
    return {
      loading: false, saving: false, hoursLoading: false,
      stores: [], stylists: [], services: [], storeHours: [], form: emptyForm(),
      weekdays: [
        { value: 0, label: '星期日' }, { value: 1, label: '星期一' },
        { value: 2, label: '星期二' }, { value: 3, label: '星期三' },
        { value: 4, label: '星期四' }, { value: 5, label: '星期五' },
        { value: 6, label: '星期六' }
      ],
      monthDays: Array.from({ length: 31 }, (_, index) => index + 1)
    };
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id); },
    ruleType() { return this.$route.params.type === 'block' || this.$route.query.type === 'block' ? 'block' : 'closure'; },
    typeLabel() { return this.ruleType === 'closure' ? '門市休假' : '預約限制'; },
    pageTitle() { return `${this.isEdit ? '編輯' : '新增'}${this.typeLabel}`; },
    blockCreateGuided() { return this.ruleType === 'block' && !this.isEdit; },
    showStoreStep() { return !this.blockCreateGuided || Boolean(this.form.name?.trim()); },
    showDateStep() { return !this.blockCreateGuided || (this.showStoreStep && Boolean(this.form.storeId)); },
    showAdvancedStep() {
      if (!this.blockCreateGuided) return true;
      if (!this.showDateStep || !this.form.startDate) return false;
      return this.form.dateMode !== 'range' || Boolean(this.form.endDate);
    },
    blockMatchesStoreClosure() {
      return this.ruleType === 'block'
        && this.form.isAllDay
        && !this.form.stylistIds.length
        && !this.form.serviceIds.length;
    },
    showBusinessSlotShortcuts() {
      return this.form.dateMode === 'single'
        && Boolean(this.form.storeId)
        && Boolean(this.form.startDate);
    },
    selectedBusinessSlots() {
      if (!this.showBusinessSlotShortcuts) return [];
      const parts = this.form.startDate.split('-').map(Number);
      if (parts.length !== 3 || parts.some(Number.isNaN)) return [];
      const dayOfWeek = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay();
      const hours = this.storeHours.find(item => Number(item.dayOfWeek) === dayOfWeek);
      if (!hours?.isOpen) return [];
      let slots = Array.isArray(hours.timeSlots) ? hours.timeSlots : [];
      if (!slots.length && hours.openTime && hours.closeTime) {
        slots = hours.breakStart && hours.breakEnd
          ? [
            { openTime: hours.openTime, closeTime: hours.breakStart },
            { openTime: hours.breakEnd, closeTime: hours.closeTime }
          ]
          : [{ openTime: hours.openTime, closeTime: hours.closeTime }];
      }
      return slots
        .filter(slot => slot?.openTime && slot?.closeTime)
        .map(slot => ({
          startTime: this.shortTime(slot.openTime),
          endTime: this.shortTime(slot.closeTime)
        }))
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
  },
  async mounted() {
    this.loading = true;
    try {
      const [stores, services, settings] = await Promise.all([
        storeService.getStores({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listServices({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listSettings()
      ]);
      const activeStores = stores?.data?.items || [];
      const openStoreIds = new Set(
        (settings?.data || [])
          .filter(item => item.bookingEnabled)
          .map(item => item.storeId)
      );
      this.stores = this.isEdit
        ? activeStores
        : activeStores.filter(store => openStoreIds.has(store.id));
      this.services = services?.data?.items || [];
      if (this.isEdit) await this.loadRule();
    } catch (error) {
      await this.handleError(error, `${this.typeLabel}載入失敗`);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadRule() {
      const response = this.ruleType === 'closure'
        ? await salonService.getClosure(this.$route.params.id)
        : await salonService.getBlock(this.$route.params.id);
      const item = response.data;
      this.form = {
        ...emptyForm(),
        ...item,
        dateMode: ['weekly', 'monthly'].includes(item.recurrenceType)
          ? 'recurring'
          : (this.dateOnly(item.startDate) === this.dateOnly(item.endDate) ? 'single' : 'range'),
        startDate: this.dateOnly(item.startDate),
        endDate: this.dateOnly(item.endDate),
        startTime: this.shortTime(item.startTime),
        endTime: this.shortTime(item.endTime),
        reason: item.reason || '',
        daysOfWeek: item.daysOfWeek || [],
        daysOfMonth: item.daysOfMonth || [],
        stylistIds: item.stylistIds || [],
        serviceIds: item.serviceIds || []
      };
      const tasks = [this.loadStoreHours(this.form.storeId)];
      if (this.ruleType === 'block') {
        tasks.push(
          this.loadStylists(this.form.storeId),
          this.loadServicesForStylists(this.form.stylistIds)
        );
      }
      await Promise.all(tasks);
    },
    async loadStoreHours(storeId) {
      if (!storeId) {
        this.storeHours = [];
        return;
      }
      this.hoursLoading = true;
      try {
        const response = await storeService.getStoreHours(storeId);
        if (this.form.storeId !== storeId) return;
        this.storeHours = response?.data?.hours || [];
      } finally {
        if (this.form.storeId === storeId) this.hoursLoading = false;
      }
    },
    async loadStylists(storeId) {
      if (!storeId) {
        this.stylists = [];
        return;
      }
      const response = await salonService.listStylists({
        page: 1, pageSize: 100, status: 'active', storeId
      });
      if (this.form.storeId !== storeId) return;
      this.stylists = response?.data?.items || [];
    },
    async loadServicesForStylists(stylistIds) {
      const ids = [...stylistIds];
      const requestKey = ids.slice().sort().join(',');
      const responses = ids.length
        ? await Promise.all(ids.map(stylistId => salonService.listServices({
          page: 1, pageSize: 100, status: 'active', stylistId
        })))
        : [await salonService.listServices({ page: 1, pageSize: 100, status: 'active' })];
      if ([...this.form.stylistIds].sort().join(',') !== requestKey) return;
      const serviceLists = responses.map(response => response?.data?.items || []);
      const commonIds = serviceLists.slice(1).reduce(
        (idsSet, list) => new Set(list.filter(service => idsSet.has(service.id)).map(service => service.id)),
        new Set((serviceLists[0] || []).map(service => service.id))
      );
      this.services = (serviceLists[0] || []).filter(service => commonIds.has(service.id));
      this.form.serviceIds = this.form.serviceIds.filter(serviceId => commonIds.has(serviceId));
    },
    async handleStoreChange() {
      this.storeHours = [];
      if (!this.form.isAllDay) {
        this.form.startTime = '';
        this.form.endTime = '';
      }
      const tasks = [this.loadStoreHours(this.form.storeId)];
      if (this.ruleType === 'block') {
        this.form.stylistIds = [];
        this.form.serviceIds = [];
        tasks.push(this.loadStylists(this.form.storeId), this.loadServicesForStylists([]));
      }
      try {
        await Promise.all(tasks);
        this.autoFillSingleBusinessSlot();
      } catch (error) {
        await this.handleError(error, '門市資料載入失敗');
      }
    },
    async handleStylistsChange() {
      this.form.serviceIds = [];
      try {
        await this.loadServicesForStylists(this.form.stylistIds);
      } catch (error) {
        await this.handleError(error, '服務項目載入失敗');
      }
    },
    payload() {
      const recurring = this.ruleType === 'closure' && this.form.dateMode === 'recurring';
      return {
        storeId: this.form.storeId,
        name: this.form.name,
        startDate: this.form.startDate,
        endDate: this.form.dateMode === 'single'
          ? this.form.startDate
          : (this.form.endDate || null),
        isAllDay: this.form.isAllDay,
        startTime: this.form.isAllDay ? null : this.form.startTime,
        endTime: this.form.isAllDay ? null : this.form.endTime,
        reason: this.form.reason || null,
        recurrenceType: recurring ? this.form.recurrenceType : 'none',
        daysOfWeek: recurring && this.form.recurrenceType === 'weekly'
          ? this.form.daysOfWeek.map(Number) : [],
        daysOfMonth: recurring && this.form.recurrenceType === 'monthly'
          ? this.form.daysOfMonth.map(Number) : [],
        recurrenceInterval: 1,
        stylistIds: this.ruleType === 'block' ? this.form.stylistIds : [],
        serviceIds: this.ruleType === 'block' ? this.form.serviceIds : []
      };
    },
    handleDateModeChange() {
      if (this.form.dateMode === 'single') {
        this.form.endDate = this.form.startDate;
        this.form.daysOfWeek = [];
        this.form.daysOfMonth = [];
      } else if (this.form.dateMode === 'range' && !this.form.endDate) {
        this.form.endDate = this.form.startDate;
      }
      if (this.form.dateMode !== 'recurring') {
        this.form.recurrenceType = 'none';
        this.form.daysOfWeek = [];
        this.form.daysOfMonth = [];
      } else if (!['weekly', 'monthly'].includes(this.form.recurrenceType)) {
        this.form.recurrenceType = 'weekly';
      }
      this.autoFillSingleBusinessSlot();
    },
    handleStartDateChange() {
      if (this.showBusinessSlotShortcuts && !this.form.isAllDay) {
        this.form.startTime = '';
        this.form.endTime = '';
        this.autoFillSingleBusinessSlot();
      }
    },
    handleAllDayChange() {
      if (!this.form.isAllDay) this.autoFillSingleBusinessSlot();
    },
    autoFillSingleBusinessSlot() {
      if (this.form.isAllDay || this.selectedBusinessSlots.length !== 1) return;
      if (this.form.startTime || this.form.endTime) return;
      this.selectBusinessSlot(this.selectedBusinessSlots[0]);
    },
    selectBusinessSlot(slot) {
      this.form.startTime = slot.startTime;
      this.form.endTime = slot.endTime;
    },
    isSelectedBusinessSlot(slot) {
      return this.form.startTime === slot.startTime && this.form.endTime === slot.endTime;
    },
    validationErrors() {
      const errors = [];
      if (!this.form.storeId) errors.push('門市：尚未選擇');
      if (!this.form.name?.trim()) errors.push('名稱：尚未輸入');
      if (!this.form.startDate) errors.push('日期：尚未選擇');
      if (this.form.dateMode === 'range' && !this.form.endDate) {
        errors.push('結束日期：尚未選擇');
      }
      if (this.form.endDate && this.form.startDate && this.form.endDate < this.form.startDate) {
        errors.push('結束日期：不可早於開始日期');
      }
      if (!this.form.isAllDay) {
        if (!this.form.startTime) errors.push('開始時間：尚未選擇');
        if (!this.form.endTime) errors.push('結束時間：尚未選擇');
        if (this.form.startTime && this.form.endTime && this.form.endTime <= this.form.startTime) {
          errors.push('結束時間：必須晚於開始時間');
        }
      }
      if (this.form.dateMode === 'recurring'
        && this.form.recurrenceType === 'weekly'
        && !this.form.daysOfWeek.length) {
        errors.push('每週休假：至少選擇一個星期');
      }
      if (this.form.dateMode === 'recurring'
        && this.form.recurrenceType === 'monthly'
        && !this.form.daysOfMonth.length) {
        errors.push('每月休假：至少選擇一個固定日');
      }
      if (this.blockMatchesStoreClosure) {
        errors.push('整日限制：未指定設計師與服務等同店休，請改用門市休假');
      }
      return errors;
    },
    async save() {
      if (this.saving) return;
      const errors = this.validationErrors();
      if (errors.length) {
        await showError('資料不足', `請補齊或修正以下資料：\n• ${errors.join('\n• ')}`);
        return;
      }
      this.saving = true;
      try {
        const payload = this.payload();
        const preview = this.ruleType === 'closure'
          ? await salonService.previewClosure(payload)
          : await salonService.previewBlock(payload);
        if ((preview.data || []).length) {
          await showError('無法儲存', `仍有 ${preview.data.length} 筆衝突預約，請先處理。`);
          return;
        }
        if (this.isEdit) {
          if (this.ruleType === 'closure') await salonService.updateClosure(this.$route.params.id, payload);
          else await salonService.updateBlock(this.$route.params.id, payload);
        } else if (this.ruleType === 'closure') {
          await salonService.createClosure(payload);
        } else {
          await salonService.createBlock(payload);
        }
        await showSuccess(this.isEdit ? '更新成功' : '建立成功', `${this.typeLabel}已儲存`);
        this.goBack();
      } catch (error) {
        await this.handleError(error, `${this.typeLabel}儲存失敗`);
      } finally {
        this.saving = false;
      }
    },
    dateOnly(value) { return salonDateOnly(value); },
    shortTime(value) { return value ? String(value).slice(0, 5) : ''; },
    goBack() { this.$router.push({ name: 'SalonExceptions', query: { type: this.ruleType } }); },
    async handleError(error, fallback) {
      await showError(fallback, error.response?.data?.message || error.message || fallback);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
.salon-form-page { max-width: 1000px; margin: 0 auto; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.check-label { display: inline-flex; align-items: center; gap: 6px; margin: 6px 18px 6px 0; }
.setup-hint { margin: 4px 0 0; }
.closure-warning { color: #b45309; margin: 0 0 12px; }
.business-slot-group { grid-column: 1 / -1; }
.business-slot-buttons { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.business-slot-button {
  border: 1px solid #556ee6;
  border-radius: 6px;
  padding: 8px 14px;
  background: #eef0ff;
  color: #405189;
  cursor: pointer;
  font-weight: 600;
}
.business-slot-button:hover,
.business-slot-button.active {
  border-color: #556ee6;
  background: #eef0ff;
  color: #405189;
}
.month-days { display: grid; grid-template-columns: repeat(auto-fill, minmax(78px, 1fr)); gap: 4px 10px; }
.month-day { margin-right: 0; }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
